/** Same-origin, authenticated delivery link for a file (`/api/files/{id}`). */
export function fileLinkForCopy(fileUrl: string): string {
  if (/^https?:\/\//.test(fileUrl) || typeof window === 'undefined') {
    return fileUrl;
  }
  return `${window.location.origin}${fileUrl}`;
}

export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    textarea.remove();
    return copied;
  } catch {
    return false;
  }
}

export function canCopyImages(): boolean {
  return (
    typeof ClipboardItem !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.clipboard?.write === 'function'
  );
}

async function toPngBlob(blob: Blob, contentType: string): Promise<Blob> {
  if (contentType === 'image/png') {
    return blob;
  }
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext('2d')?.drawImage(bitmap, 0, 0);
  bitmap.close();
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      result => (result ? resolve(result) : reject(new Error('encode'))),
      'image/png'
    );
  });
}

/**
 * Copies an image to the clipboard as PNG. The ClipboardItem is created
 * synchronously inside the user gesture with a Blob promise, which is what
 * Safari requires; the fetch happens afterwards.
 */
export async function copyImageFromUrl(
  url: string,
  contentType: string
): Promise<boolean> {
  if (!canCopyImages()) {
    return false;
  }
  try {
    const blobPromise = fetch(url, { credentials: 'same-origin' })
      .then(response => {
        if (!response.ok) {
          throw new Error(String(response.status));
        }
        return response.blob();
      })
      .then(blob => toPngBlob(blob, contentType));
    await navigator.clipboard.write([
      new ClipboardItem({ 'image/png': blobPromise })
    ]);
    return true;
  } catch {
    return false;
  }
}
